import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Box, ButtonBase, Grid, Typography } from "@mui/material";
import { FormikProps } from "formik";
import { NewPatientForm } from "../../../../../../constants/provider/patient/add-new-patient/add-new-patient-form";
import { useDebounce } from "../../../../../../hooks/use-debounce";
import { useReduceDisclosure } from "../../../../../../hooks/use-reduce-disclosure";
import { useGetPharmacyByType } from "../../../../../../queries/patient-chart/order/use-get-pharmacy-by-type";
import {
  applyDefaultColumnSettings,
  getAddressFormat,
  getXtenantId,
} from "../../../../../../utils/utils";
import CustomDataGridV2 from "../../../../../CustomDataGridV2";
import Flex from "../../../../../Flex";
import OutlineCard from "../../../../../OutlineCard";
import RenderWithTooltip from "../../../../../RenderWithTooltip";
import CustomTitle from "../../../../CustomTitle";
import StyledButtonV2 from "../../../../StyledButtonV2";
import PatientLabPharmacyRadiologyFormDrawer from "./PatientLabPharmacyRadiologyFormDrawer";
import MaskMultiSelect from "../../../../../MaskMultiSelect";

interface PreferencesProps {
  formik: FormikProps<NewPatientForm>;
}

const Preferences = ({ formik }: PreferencesProps) => {
  const [commonSearchKeys, _] = useDebounce<string>("");
  // const [searchValue, setSearchValue] = useState<{
  //   value: string;
  //   label: string;
  // } | null>();

  const {
    data: allPreferencesList,
    isLoading,
    isFetching,
  } = useGetPharmacyByType({
    pagination: { page: 0, pageSize: 100 },
    searchString: commonSearchKeys,
    xTENANTID: getXtenantId(),
  });

  const PREFERENCES_COLUMN = applyDefaultColumnSettings<any>([
    {
      field: "name",
      flex: 0.4,
      minWidth: 80,
      headerName: "Name",
      renderCell: ({ row }) => row?.name || "-",
    },
    {
      field: "preferenceType",
      headerName: "Type",
      flex: 0.4,
      minWidth: 110,
      renderCell: ({ value }) => value || "-",
    },
    {
      field: "address",
      flex: 0.6,
      headerName: "Address",
      renderCell: ({ value }) => (
        <RenderWithTooltip value={getAddressFormat(value) || "-"} />
      ),
    },
    {
      field: "faxNumber",
      flex: 0.4,
      headerName: "Fax Number",
      disableReorder: false,
      disableColumnMenu: true,
      renderCell: ({ value }) => value || "-",
    },
    {
      field: "contactNumber",
      flex: 0.4,
      headerName: "Contact Number",
      disableReorder: false,
      disableColumnMenu: true,
      renderCell: ({ value }) => value || "-",
    },
    {
      field: "Action",
      width: 90,
      hideSortIcons: true,
      renderCell: (params) => (
        <Box sx={{ width: "100%" }}>
          <ButtonBase
            onClick={() => {
              const prevValues =
                formik.values.pharmacyLabRadiologyMap?.toSpliced(
                  params.api.getAllRowIds().indexOf(params.id),
                  1
                );
              formik.setFieldValue("pharmacyLabRadiologyMap", prevValues);
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                fontWeight: "500",
              }}
            >
              <DeleteOutlineIcon color={"error"} sx={{ cursor: "pointer" }} />
            </Typography>
          </ButtonBase>
        </Box>
      ),
    },
  ]);

  const [
    // addPharmacyDisclosure,
    addLabDisclosure,
    // addRadiologyDisclosure
  ] = useReduceDisclosure(3);

  return (
    <Box id={"Preferences"} sx={{ minWidth: 0, width: "100%" }}>
      <CustomTitle mb={1}>Preferences</CustomTitle>
      <OutlineCard>
        <Grid
          container
          sx={{
            gap: 2,
            p: 0.8,
            flexDirection: "column",
          }}
        >
          <PatientLabPharmacyRadiologyFormDrawer
            opened={addLabDisclosure.opened}
            handleCloseModal={addLabDisclosure.close}
          />
          <Flex justifyContent={"space-between"} alignItems={"center"} gap={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <MaskMultiSelect
                label="Preferences"
                // onSearch={setCommonSearchKeys}
                options={allPreferencesList?.data?.content}
                optionWithCheckbox={true}
                value={(formik.values as any)?.pharmacyLabRadiologyMap}
                onChange={(values) => {
                  formik.setFieldValue("pharmacyLabRadiologyMap", values);
                }}
              />
            </Box>
            <Box sx={{ flexShrink: 0 }}>
              <StyledButtonV2
                variant="text"
                onClick={() => addLabDisclosure.open()}
                label="Add New Preferences"
                shouldAddIcon
              />
            </Box>
          </Flex>
        </Grid>
        {!!(formik.values as any)?.pharmacyLabRadiologyMap?.length && (
          <Box sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>
            <CustomDataGridV2
              loading={isLoading || isFetching}
              rowHeight={28}
              hideFooter
              getRowId={(row) => row.uuid}
              rows={(formik.values as any)?.pharmacyLabRadiologyMap ?? []}
              columns={PREFERENCES_COLUMN}
              disableRowSelectionOnClick
            />
          </Box>
        )}
      </OutlineCard>
    </Box>
  );
};

export default Preferences;
